import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import PageTitle from "../../components/PageTitle";
import dayjs from "dayjs";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import apiClient from "../../utils/apiClient";
import { DataGrid } from "@mui/x-data-grid";
import { brandColor } from "../../theme";
import type { GridColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Act, ActType } from "../../types/act";
import { RootState } from "../../store";
import { ActPrintType, ActViewer } from "../../components/ActViewer";
import ActStatus from "../../enum/ActStatus.enum";
import { GridRenderCellParams } from "@mui/x-data-grid/models/params/gridCellParams";
import ActAction from "../../enum/ActAction.enum";
import Provider from "../../enum/Provider.enum";
import ActTypeEnum from "../../enum/ActType.enum";
import DetailingCpaTab from "../DetailingCpaTab";
import DetailingAm from "./Detailing.am";
import StyledDrawer from "../../components/StyledDrawer";
import AgentDetailingClients from "../../components/AgentDetailingClients";

const TAB_BRA = 0;
const TAB_CPA = 1;

const DetailingDefault = () => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<number>(TAB_BRA);

  const { isAdmin, user } = useSelector((state: RootState) => state.user);
  const { provider } = useSelector((state: RootState) => state.app);

  const [month, setMonth] = useState<string>(dayjs().format("MM"));
  const [year, setYear] = useState<string>(dayjs().format("YYYY"));
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<any[]>();
  const [currency, setCurrency] = useState<string>();

  const [isLoadingActs, setIsLoadingActs] = useState<boolean>(false);
  const [acts, setActs] = useState<Record<string, Act>>();
  const [actTypeMap, setActTypeMap] = useState<Record<string, ActType>>();
  const [selectedAct, setSelectedAct] = useState<Act>();
  const [actType, setActType] = useState<ActPrintType>(ActPrintType.ACT);
  const [selectedAgent, setSelectedAgent] = useState<{
    id: string;
    fullname: string;
  } | null>(null);

  const storedUser = localStorage.getItem("user");

  const schemeTypeLower = useMemo(() => {
    try {
      const parsed = storedUser ? JSON.parse(storedUser) : null;
      return String(parsed?.scheme_type ?? "").toLowerCase();
    } catch {
      return "";
    }
  }, [storedUser]);

  const isBelarusProvider = useMemo(() => {
    return (
      String(provider ?? "").toLowerCase() ===
      String(Provider.BELARUS).toLowerCase()
    );
  }, [provider]);

  const canSeeBraTab = useMemo(() => {
    if (isAdmin) return true;
    return schemeTypeLower === "default";
  }, [isAdmin, schemeTypeLower]);

  const canSeeCpaTab = useMemo(() => {
    if (!isBelarusProvider) return false;
    if (isAdmin) return true;
    return schemeTypeLower === "referral";
  }, [isBelarusProvider, isAdmin, schemeTypeLower]);

  useEffect(() => {
    const availableTabs: number[] = [];
    if (canSeeBraTab) availableTabs.push(TAB_BRA);
    if (canSeeCpaTab) availableTabs.push(TAB_CPA);

    if (!availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0] ?? TAB_BRA);
    }
  }, [canSeeBraTab, canSeeCpaTab, activeTab]);

  const loadActs = async () => {
    setIsLoadingActs(true);

    try {
      const responseActs = await apiClient.get(
        `/acts?month=${month}&year=${year}`,
      );

      if (responseActs.data.success) {
        const actsByTnId: Record<string, Act> = {};

        responseActs.data.rows.forEach((act: Act) => {
          if (act.tn_id) {
            actsByTnId[act.tn_id] = act;
          }
        });

        setActs(actsByTnId);
        setActTypeMap(responseActs.data.type);

        if (selectedAct && selectedAct?.id > 0) {
          setSelectedAct(actsByTnId[selectedAct.tn_id]);
        }
      }
    } catch (error) {
      console.error("Ошибка запроса актов:", error);
    } finally {
      setIsLoadingActs(false);
    }
  };

  const columns = React.useMemo<GridColDef<any>[]>(() => {
    const cols: GridColDef[] = [
      {
        field: "fullname",
        headerName: isAdmin
          ? t("detailing.columns.agentFio")
          : t("detailing.columns.fio"),
        flex: 2,
        ...(isAdmin
          ? {
              renderCell: (params: GridRenderCellParams) => (
                <Box
                  sx={{
                    cursor: "pointer",
                    color: "primary.main",
                    width: "100%",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedAgent({
                      id: String(params.row.id),
                      fullname: params.row.fullname,
                    });
                  }}
                >
                  {params.value}
                </Box>
              ),
            }
          : {}),
      },
      {
        field: "brokerage",
        headerName: t("detailing.columns.brokerage"),
        flex: 1,
        type: "number",
      },
      {
        field: "retention",
        headerName: t("detailing.columns.retention"),
        flex: 1,
        type: "number",
      },
      {
        field: "acquisition",
        headerName: t("detailing.columns.acquisition"),
        flex: 1,
        type: "number",
      },
      {
        field: "total",
        headerName: t("detailing.columns.total"),
        flex: 1,
        type: "number",
      },
    ];

    if (isAdmin && provider === Provider.BELARUS) {
      cols.push({
        field: "actions",
        headerName: "",
        type: "actions",
        width: 160,
        renderCell: (params: GridRenderCellParams) => {
          if (!isAdmin) return null;

          const act = acts?.[params.row?.id];
          if (!act) return null;

          const currentActType = actTypeMap?.[act.type_id];

          const actIcon =
            act?.status === ActStatus.APPROVED ? (
              <TaskAltIcon color="success" />
            ) : (
              <RadioButtonUncheckedIcon color="disabled" />
            );

          return (
            <Stack spacing={1} direction="row">
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  setActType(ActPrintType.ACT);
                  setSelectedAct(act);
                }}
                startIcon={actIcon}
                loading={isLoadingActs}
              >
                {t("detailing.act")}
              </Button>

              {currentActType?.code === ActTypeEnum.DEFAULT && (
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActType(ActPrintType.REPORT);
                    setSelectedAct(act);
                  }}
                  loading={isLoadingActs}
                  disabled={act?.status !== ActStatus.APPROVED}
                >
                  {t("detailing.report")}
                </Button>
              )}
            </Stack>
          );
        },
      });
    }

    return cols;
  }, [acts, actTypeMap, isAdmin, provider, isLoadingActs, t]);

  const handleGetReport = async () => {
    try {
      setIsLoading(true);

      const response = await apiClient.get(
        `/detailing?month=${month}&year=${year}`,
      );

      if (response.data.success) {
        setRows(response.data.rows);
        setCurrency(response.data.currency);
      } else {
        console.error("Ошибка получения детализации");
      }

      await loadActs();
    } catch (error) {
      console.error("Ошибка получения детализации");
    } finally {
      setIsLoading(false);
      setIsLoadingActs(false);
    }
  };

  const years = Array.from(Array(5).keys()).map(
    (i) => +dayjs().format("YYYY") - i,
  );

  const total = useMemo(() => {
    if (!rows?.length) return {};

    const r = rows.reduce(
      (acc, row) => ({
        brokerage: acc.brokerage + row.brokerage,
        retention: acc.retention + row.retention,
        acquisition: acc.acquisition + row.acquisition,
        total: acc.total + row.total,
      }),
      { brokerage: 0, retention: 0, acquisition: 0, total: 0 },
    );

    return {
      brokerage: r.brokerage.toFixed(2),
      retention: r.retention.toFixed(2),
      acquisition: r.acquisition.toFixed(2),
      total: r.total.toFixed(2),
    };
  }, [rows]);

  return (
    <>
      <Helmet>
        <title>{t("detailing.metaTitle")}</title>
        <meta name="description" content={t("detailing.metaDescription")} />
      </Helmet>

      <PageTitle>{t("detailing.title")}</PageTitle>

      <Box sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => {
            const next = Number(v);
            if (next === TAB_BRA && !canSeeBraTab) return;
            if (next === TAB_CPA && !canSeeCpaTab) return;
            setActiveTab(next);
          }}
          sx={{
            borderBottom: "1px solid #eee",
            "& .MuiTab-root": { textTransform: "none" },
          }}
        >
          {canSeeBraTab && (
            <Tab label={t("detailing.tabs.bra")} value={TAB_BRA} />
          )}

          {canSeeCpaTab && (
            <Tab label={t("detailing.tabs.cpa")} value={TAB_CPA} />
          )}
        </Tabs>
      </Box>

      {canSeeBraTab && activeTab === TAB_BRA && (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <FormControl sx={{ minWidth: 150, mr: 1 }} size="small">
              <InputLabel id="detailing-select-month-label">
                {t("detailing.month")}
              </InputLabel>

              <Select
                labelId="detailing-select-month-label"
                id="detailing-select-month"
                value={month}
                label={t("detailing.month")}
                size="small"
                onChange={(e) => setMonth(e.target.value)}
                variant="outlined"
              >
                <MenuItem value="01">{t("detailing.months.january")}</MenuItem>
                <MenuItem value="02">{t("detailing.months.february")}</MenuItem>
                <MenuItem value="03">{t("detailing.months.march")}</MenuItem>
                <MenuItem value="04">{t("detailing.months.april")}</MenuItem>
                <MenuItem value="05">{t("detailing.months.may")}</MenuItem>
                <MenuItem value="06">{t("detailing.months.june")}</MenuItem>
                <MenuItem value="07">{t("detailing.months.july")}</MenuItem>
                <MenuItem value="08">{t("detailing.months.august")}</MenuItem>
                <MenuItem value="09">
                  {t("detailing.months.september")}
                </MenuItem>
                <MenuItem value="10">{t("detailing.months.october")}</MenuItem>
                <MenuItem value="11">{t("detailing.months.november")}</MenuItem>
                <MenuItem value="12">{t("detailing.months.december")}</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 150, mr: 1 }} size="small">
              <InputLabel id="detailing-select-year-label">
                {t("detailing.year")}
              </InputLabel>

              <Select
                labelId="detailing-select-year-label"
                id="detailing-select-year"
                value={year}
                label={t("detailing.year")}
                size="small"
                onChange={(e) => setYear(e.target.value)}
                variant="outlined"
              >
                {years.map((y) => (
                  <MenuItem key={y} value={String(y)}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              color="primary"
              onClick={handleGetReport}
            >
              {t("detailing.get")}
            </Button>

            {!isAdmin &&
              provider === Provider.BELARUS &&
              acts?.[user?.user_id_tn ?? ""] && (
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    setActType(ActPrintType.ACT);
                    setSelectedAct(acts?.[user?.user_id_tn ?? ""]);
                  }}
                  startIcon={<FileDownloadIcon />}
                  loading={isLoadingActs}
                  disabled={
                    acts?.[user?.user_id_tn ?? ""]?.status !==
                    ActStatus.APPROVED
                  }
                >
                  {t("detailing.act")}
                </Button>
              )}
          </Stack>

          <Box style={{ display: "flex", flexDirection: "column" }}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              sx={{
                p: 1,
                borderLeft: "3px solid " + brandColor,
                backgroundColor: "#FFFDE7",
                mb: 1,
              }}
            >
              <Typography variant="body2">
                <b>{t("detailing.total")}</b>
                <CurrencyLabel currency={currency ?? ""} />
              </Typography>

              <Typography variant="body2">
                {t("detailing.columns.brokerage")}:{" "}
                <strong>{total?.brokerage ?? 0}</strong>
              </Typography>

              <Typography variant="body2">
                {t("detailing.columns.retention")}:{" "}
                <strong>{total?.retention ?? 0}</strong>
              </Typography>

              <Typography variant="body2">
                {t("detailing.columns.acquisition")}:{" "}
                <strong>{total?.acquisition ?? 0}</strong>
              </Typography>

              <Typography variant="body2">
                {t("detailing.columns.total")}:{" "}
                <strong>{total?.total ?? 0}</strong>
              </Typography>
            </Stack>

            <DataGrid
              rows={rows ?? []}
              columns={columns}
              loading={isLoading}
              pagination
              onRowClick={
                isAdmin
                  ? (params) => {
                      setSelectedAgent({
                        id: String(params.row.id),
                        fullname: params.row.fullname,
                      });
                    }
                  : undefined
              }
              sx={{
                ...(isAdmin
                  ? {
                      "& .MuiDataGrid-row": { cursor: "pointer" },
                    }
                  : {}),
                "--DataGrid-rowBorderColor": "#D1D3D4",
                "& .MuiDataGrid-columnHeaderTitleContainer": {
                  fontSize: 12,
                  lineHeight: "110%",
                },
                "& .MuiDataGrid-cell": {
                  fontSize: 16,
                  borderTop: "0.5px solid #D1D3D4",
                },
                fontFamily: "inherit",
                borderWidth: 0,
                background: "#ffffff",
              }}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
            />
          </Box>
        </>
      )}

      {canSeeCpaTab && activeTab === TAB_CPA && <DetailingCpaTab />}

      <StyledDrawer
        show={Boolean(isAdmin && selectedAgent)}
        width="80vw"
        title={t("detailing.agentDrawerTitle", {
          name: selectedAgent?.fullname,
          id: selectedAgent?.id,
        })}
        onClose={() => setSelectedAgent(null)}
        ContentComponent={AgentDetailingClients}
        contentProps={{
          agentTnId: selectedAgent?.id ?? "",
          agentName: selectedAgent?.fullname ?? "",
          month,
          year,
        }}
      />

      {selectedAct && selectedAct?.id > 0 && (
        <ActViewer
          act={selectedAct}
          onClose={() => {
            setSelectedAct(undefined);
          }}
          onAction={(action: ActAction) => {
            loadActs();
          }}
          type={actType}
        />
      )}
    </>
  );
};

const CurrencyLabel = ({ currency }: { currency: string }) => {
  const { t } = useTranslation();

  const [label, setLabel] = useState<string>();

  useEffect(() => {
    let res;

    switch (currency) {
      case "RUR":
        res = t("detailing.currency.rur");
        break;
      case "USD":
        res = "$";
        break;
      default:
        res = currency;
    }

    setLabel(res);
  }, [currency, t]);

  if (!currency) {
    return null;
  }

  return (
    <span title={currency}>
      {t("detailing.currency.in", { currency: label })}
    </span>
  );
};

const Detailing = () => {
  const { provider } = useSelector((state: RootState) => state.app);

  if ([Provider.ARMENIA, Provider.UAE, Provider.TFOS].includes(provider)) {
    return <DetailingAm />;
  }

  return <DetailingDefault />;
};

export default Detailing;
